import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface DVLALicenseRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DVLALicenseRequestDialog: React.FC<DVLALicenseRequestDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [recipientEmailsText, setRecipientEmailsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const handleClose = () => {
    if (!loading) {
      setRecipientEmailsText('');
      setError(null);
      setSuccess(false);
      setConsentConfirmed(false);
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!recipientEmailsText.trim()) {
      setError('Recipient email(s) are required');
      return;
    }

    if (!consentConfirmed) {
      setError('You must confirm that you have consent to request this check');
      return;
    }

    // Parse and validate email list (comma or newline separated)
    const emails = recipientEmailsText
      .split(/[\n,]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0);

    if (emails.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    // Validate email formats
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      setError(`These email(s) are invalid: ${invalidEmails.join(', ')}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/dvla-license-requests/request`,
        {
          recipientEmails: emails
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(true);
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating license check request:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to create license check request'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Driving License Check</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            License check request sent successfully! An email has been sent to the recipient.
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter one or more email addresses for the people whose driving licenses you want to check. 
            They will each receive an email with a secure link to submit their license details.
          </Typography>

          <TextField
            fullWidth
            label="Recipient Email(s) *"
            value={recipientEmailsText}
            onChange={(e) => setRecipientEmailsText(e.target.value)}
            disabled={loading || success}
            sx={{ mb: 2 }}
            placeholder={'john@example.com\njane@example.com\nor: john@example.com, jane@example.com'}
            multiline
            minRows={3}
          />

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Important Information:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>The request will expire in 7 days</li>
              <li>One credit will be reserved for this request</li>
              <li>The credit will be deducted when they submit their details</li>
              <li>You must have consent from the individual before requesting their license check</li>
            </ul>
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={consentConfirmed}
                onChange={(e) => setConsentConfirmed(e.target.checked)}
                disabled={loading || success}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I confirm that I have obtained consent from this individual to request their driving license check
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success || !consentConfirmed}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Sending...' : 'Send Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DVLALicenseRequestDialog;

